import {
  classifyCapgoReason,
  extractTargetVersion,
  isContentReason,
  stripCapgoPrefix,
  stripUrlQueries,
} from 'src/modules/boot/capgoReason';

export type CapgoLineFamily =
  | 'manifest-file-failed'
  | 'manifest-file-network'
  | 'manifest-file-timeout'
  | 'manifest-file-error'
  | 'echo-download-failed'
  | 'echo-failed-bundle'
  | 'zip-timeout'
  | 'manifest-batch-failed'
  | 'manifest-handler'
  | 'echo-waiting'
  | 'download-failed'
  | 'zip-download-error'
  | 'disk-precheck'
  | 'notify-app-ready-rollback'
  | 'reload-failed';

export interface CapgoLineVerdict {
  family: CapgoLineFamily;
  drop: boolean;
  fingerprint: string[];
  message: string;
  tags: Record<string, string>;
  extra: Record<string, unknown>;
}

interface DropRule {
  family: CapgoLineFamily;
  pattern: RegExp;
  message: (match: RegExpExecArray) => string;
}

const MESSAGE_STEM = 'CapgoUpdater: ';

const constant = (text: string) => () => text;

// Drop families first; each pattern is anchored at both ends and has no nested quantifier.
const DROP_RULES: DropRule[] = [
  {
    family: 'manifest-file-failed',
    pattern: /^Manifest file download failed(?:: (\S+))?$/,
    message: (match) =>
      match[1] === undefined
        ? 'Manifest file download failed'
        : 'Manifest file download failed: <file>',
  },
  {
    family: 'manifest-file-network',
    pattern: /^Manifest file download network error$/,
    message: constant('Manifest file download network error'),
  },
  {
    family: 'manifest-file-timeout',
    pattern: /^downloadManifestFile (\S+) timed out after (\d+)s$/,
    message: (match) => `downloadManifestFile <file> timed out after ${match[2]}s`,
  },
  {
    family: 'manifest-file-error',
    pattern: /^Error processing file: (\S+) Error in downloadAndVerify: [\s\S]+$/,
    message: constant('Error processing file: <file> Error in downloadAndVerify'),
  },
  {
    family: 'echo-download-failed',
    pattern: /^Download failed$/,
    message: constant('Download failed'),
  },
  {
    family: 'echo-failed-bundle',
    pattern: /^Failed to download bundle$/,
    message: constant('Failed to download bundle'),
  },
  {
    family: 'zip-timeout',
    pattern: /^download (\S+) timed out after \d+s$/,
    message: (match) => match[0],
  },
];

type ReasonFamily = 'download-failed' | 'zip-download-error' | 'manifest-handler' | 'echo-waiting';

const REASON_LABELS: Record<ReasonFamily, string> = {
  'download-failed': 'Failed to download from: <bundle-url>',
  'zip-download-error': 'Download error:',
  'manifest-handler': 'Error in handleManifestDownload',
  'echo-waiting': 'Error waiting for download',
};

const SEMVER = /^\d+\.\d+\.\d+$/;
const HTTP_RANGE_NOT_SATISFIABLE = 416;
const MAX_ID_LENGTH = 120;
const MANIFEST_BATCH_ECHO = 'One or more files failed to download';

export const fnv1a32 = (text: string): string => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash = Math.imul(hash ^ text.charCodeAt(index), 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

const keptVerdict = (
  family: CapgoLineFamily,
  fingerprint: string[],
  message: string,
  tags: Record<string, string> = {},
  extra: Record<string, unknown> = {},
): CapgoLineVerdict => ({
  family,
  drop: false,
  fingerprint,
  message: MESSAGE_STEM + message,
  tags: { capgo_family: family, ...tags },
  extra,
});

const buildReasonVerdict = (
  family: ReasonFamily,
  reasonText: string,
  urlText?: string,
): CapgoLineVerdict => {
  const { reasonClass, httpStatus, reasonRaw } = classifyCapgoReason(reasonText.replace(/\s+/g, ' '));
  const targetVersion = extractTargetVersion(urlText === undefined ? reasonText : stripUrlQueries(urlText));
  const fingerprint = ['capgo', family, reasonClass];
  // A 416 is a device-local resume error, so it must not open a new issue per release.
  if (isContentReason(reasonClass) && httpStatus !== HTTP_RANGE_NOT_SATISFIABLE) {
    fingerprint.push(targetVersion ?? 'no-version');
  }
  if (reasonClass === 'unclassified') fingerprint.push(`h:${fnv1a32(reasonRaw)}`);
  const tags: Record<string, string> = { capgo_reason: reasonClass };
  if (targetVersion !== undefined && SEMVER.test(targetVersion)) tags.capgo_target_version = targetVersion;
  if (httpStatus !== undefined) tags.capgo_http_status = String(httpStatus);
  return keptVerdict(family, fingerprint, `${REASON_LABELS[family]} (${reasonClass})`, tags, {
    reason_raw: reasonRaw,
  });
};

interface KeptRule {
  pattern: RegExp;
  build: (match: RegExpExecArray) => CapgoLineVerdict;
}

const dropVerdict = (family: CapgoLineFamily, message: string): CapgoLineVerdict => ({
  family,
  drop: true,
  fingerprint: ['capgo', family],
  message: MESSAGE_STEM + message,
  tags: { capgo_family: family },
  extra: {},
});

// manifest-handler and echo-waiting decide drop vs keep here, so no drop rule can shadow them.
const KEPT_RULES: KeptRule[] = [
  {
    pattern: /^One or more files failed to download$/,
    build: () => keptVerdict('manifest-batch-failed', ['capgo', 'manifest-batch-failed'], MANIFEST_BATCH_ECHO),
  },
  {
    pattern: /^Error in handleManifestDownload ([\s\S]+)$/,
    build: (match) =>
      match[1] === MANIFEST_BATCH_ECHO
        ? dropVerdict('manifest-handler', match[0])
        : buildReasonVerdict('manifest-handler', match[1]),
  },
  {
    pattern: /^Error waiting for download(?: ([\s\S]+))?$/,
    build: (match) =>
      match[1] === undefined
        ? dropVerdict('echo-waiting', match[0])
        : buildReasonVerdict('echo-waiting', match[1]),
  },
  {
    pattern: /^Failed to download from: (\S+)(?: ([\s\S]+))?$/,
    build: (match) => buildReasonVerdict('download-failed', match[2] ?? match[0], match[1]),
  },
  {
    pattern: /^Download error: ([\s\S]+)$/,
    build: (match) => buildReasonVerdict('zip-download-error', match[1]),
  },
  {
    pattern: /^Insufficient disk space\. Available: (\d+), Required: (\d+)$/,
    build: (match) =>
      keptVerdict(
        'disk-precheck',
        ['capgo', 'disk-precheck'],
        'Insufficient disk space',
        { capgo_reason: 'disk_precheck' },
        { capgo_disk_available: Number(match[1]), capgo_disk_required: Number(match[2]) },
      ),
  },
  {
    pattern: /^notifyAppReady was not called, roll back current bundle: ([\s\S]+)$/,
    build: (match) =>
      keptVerdict(
        'notify-app-ready-rollback',
        ['capgo', 'notify-app-ready-rollback'],
        'notifyAppReady was not called, roll back current bundle: <id>',
        {},
        { capgo_id: match[1].slice(0, MAX_ID_LENGTH) },
      ),
  },
  {
    pattern: /^Reload failed after (setting bundle|applying pending bundle:) ([\s\S]+)$/,
    build: (match) =>
      keptVerdict(
        'reload-failed',
        ['capgo', 'reload-failed', match[1] === 'setting bundle' ? 'set' : 'pending'],
        `Reload failed after ${match[1]} <id>`,
        {},
        { capgo_id: match[2].slice(0, MAX_ID_LENGTH) },
      ),
  },
];

export const classifyCapgoLine = (arg0: string): CapgoLineVerdict | null => {
  if (typeof arg0 !== 'string') return null;
  const body = stripCapgoPrefix(arg0.normalize('NFC'));
  if (body === null) return null;
  for (const rule of DROP_RULES) {
    const match = rule.pattern.exec(body);
    if (match !== null) {
      return dropVerdict(rule.family, rule.message(match));
    }
  }
  for (const rule of KEPT_RULES) {
    const match = rule.pattern.exec(body);
    if (match !== null) return rule.build(match);
  }
  return null;
};
